// CB:BEGIN fr-034-admin-realtors-page
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getApiBaseUrl } from '../../lib/getApiUrl';
import { CheckCircle, AlertTriangle, XCircle, Clock, DollarSign, ChevronRight, Loader2, RefreshCw } from 'lucide-react';

const BRAND = '#123c3b';

interface RealtorSummary {
  id: string;
  userId: string;
  email: string;
  brokerageName?: string | null;
  status: string;
  riskScore: number;
  abuseFlag: boolean;
  verifiedAt?: string | null;
  quoteCount: number;
  incentiveSummary: Record<string, number>;
  createdAt: string;
}

interface IncentiveLedgerRow {
  id: string;
  realtorUserId: string;
  projectId: string;
  status: string;
  amountCents: number;
  eligibleAt?: string | null;
  holdReleasesAt?: string | null;
  approvedAt?: string | null;
  paidAt?: string | null;
  clawedBackAt?: string | null;
  realtorUser?: { id: string; email: string; realtorProfile?: { brokerageName?: string | null; status: string } | null };
  project?: { id: string; address: string };
  dispute?: { id: string; status: string; reason: string } | null;
}

const STATUS_BADGE: Record<string, { bg: string; color: string; label: string }> = {
  UNVERIFIED: { bg: '#fef9c3', color: '#854d0e', label: 'Unverified' },
  ACTIVE: { bg: '#dcfce7', color: '#166534', label: 'Active' },
  SUSPENDED: { bg: '#fee2e2', color: '#991b1b', label: 'Suspended' },
  TERMINATED: { bg: '#f1f5f9', color: '#475569', label: 'Terminated' },
  PENDING_CONVERSION: { bg: '#f1f5f9', color: '#475569', label: 'Pending' },
  CONVERTED: { bg: '#dbeafe', color: '#1e40af', label: 'Converted' },
  ELIGIBLE: { bg: '#fef9c3', color: '#854d0e', label: 'Eligible' },
  APPROVED: { bg: '#ede9fe', color: '#5b21b6', label: 'Approved' },
  PAID: { bg: '#dcfce7', color: '#166534', label: 'Paid' },
  CLAWED_BACK: { bg: '#fee2e2', color: '#991b1b', label: 'Clawed Back' },
  VOID: { bg: '#f1f5f9', color: '#94a3b8', label: 'Void' },
};

function Badge({ status }: { status: string }) {
  const s = STATUS_BADGE[status] ?? { bg: '#f1f5f9', color: '#475569', label: status };
  return (
    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 700, background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

interface DisputeRow {
  id: string;
  status: string;
  reason: string;
  createdAt: string;
  realtorUser?: { id: string; email: string } | null;
  ledger?: { id: string; status: string; amountCents: number; projectId: string } | null;
}

const AdminRealtorsPage: React.FC<{ onNavigate?: (screen: string) => void }> = ({ onNavigate }) => {
  const { getToken } = useAuth();
  const [view, setView] = useState<'list' | 'incentives' | 'disputes'>('list');
  const [realtors, setRealtors] = useState<RealtorSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [incentives, setIncentives] = useState<IncentiveLedgerRow[]>([]);
  const [incentivesLoading, setIncentivesLoading] = useState(false);
  const [incentiveFilter, setIncentiveFilter] = useState('');
  const [disputes, setDisputes] = useState<DisputeRow[]>([]);
  const [disputesLoading, setDisputesLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  };

  const apiCall = useCallback(async (path: string, options?: RequestInit) => {
    const token = await getToken();
    const base = getApiBaseUrl().replace(/\/$/, '');
    const res = await fetch(`${base}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options?.headers ?? {}),
      },
    });
    const json = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, data: json };
  }, [getToken]);

  const fetchRealtors = useCallback(async () => {
    setLoading(true);
    try {
      const { ok, data } = await apiCall('/api/admin/realtors');
      if (ok) setRealtors(data.realtors || []);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  const fetchIncentives = useCallback(async (status?: string) => {
    setIncentivesLoading(true);
    try {
      const qs = status ? `?status=${status}` : '';
      const { ok, data } = await apiCall(`/api/admin/realtors/incentives${qs}`);
      if (ok) setIncentives(data.ledger || []);
    } finally {
      setIncentivesLoading(false);
    }
  }, [apiCall]);

  const fetchDisputes = useCallback(async () => {
    setDisputesLoading(true);
    try {
      const { ok, data } = await apiCall('/api/admin/realtors/disputes');
      if (ok) setDisputes(data.disputes || []);
    } finally {
      setDisputesLoading(false);
    }
  }, [apiCall]);

  const export1099 = async () => {
    const year = new Date().getFullYear();
    const token = await getToken();
    const base = (window as any).__API_BASE__ || '';
    const url = `${base}/api/admin/realtors/tax/1099-export?year=${year}`;
    const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) { showToast('1099 export failed', false); return; }
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `1099-${year}.csv`;
    a.click();
  };

  const w9Action = async (userId: string, action: 'verify' | 'reject') => {
    if (action === 'reject') {
      const reason = window.prompt('Rejection reason (required):');
      if (!reason?.trim()) return;
      setActionLoading(`${userId}-w9-reject`);
      const { ok, data } = await apiCall(`/api/admin/realtors/${userId}/w9/reject`, { method: 'POST', body: JSON.stringify({ reason: reason.trim() }) });
      setActionLoading(null);
      ok ? showToast('W-9 rejected.') : showToast(data.error || 'Failed', false);
      fetchRealtors();
    } else {
      setActionLoading(`${userId}-w9-verify`);
      const { ok, data } = await apiCall(`/api/admin/realtors/${userId}/w9/verify`, { method: 'POST' });
      setActionLoading(null);
      ok ? showToast('W-9 verified!') : showToast(data.error || 'Failed', false);
      fetchRealtors();
    }
  };

  const resolveDispute = async (disputeId: string) => {
    const decision = window.prompt('Decision / reasoning (required):');
    if (!decision?.trim()) return;
    const upheldStr = window.prompt('Uphold the dispute? (yes/no)');
    if (!upheldStr) return;
    const upheld = upheldStr.trim().toLowerCase().startsWith('y');
    setActionLoading(`dispute-${disputeId}`);
    const { ok, data } = await apiCall(`/api/admin/realtors/disputes/${disputeId}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ decision: decision.trim(), upheld }),
    });
    setActionLoading(null);
    ok ? showToast(`Dispute ${upheld ? 'upheld' : 'denied'}.`) : showToast(data.error || 'Failed', false);
    fetchDisputes();
  };

  useEffect(() => { fetchRealtors(); }, [fetchRealtors]);
  useEffect(() => { if (view === 'incentives') fetchIncentives(incentiveFilter || undefined); }, [view, incentiveFilter, fetchIncentives]);
  useEffect(() => { if (view === 'disputes') fetchDisputes(); }, [view, fetchDisputes]);

  const realtorAction = async (userId: string, action: string, body?: object) => {
    const key = `${userId}-${action}`;
    setActionLoading(key);
    try {
      const { ok, data } = await apiCall(`/api/admin/realtors/${userId}/${action}`, { method: 'POST', body: body ? JSON.stringify(body) : undefined });
      if (ok) { showToast(`${action} succeeded.`); fetchRealtors(); }
      else showToast(data.error || `${action} failed.`, false);
    } finally {
      setActionLoading(null);
    }
  };

  const incentiveAction = async (ledgerId: string, action: string, body?: object) => {
    setActionLoading(`${ledgerId}-${action}`);
    try {
      const { ok, data } = await apiCall(`/api/admin/realtors/incentives/${ledgerId}/${action}`, { method: 'POST', body: body ? JSON.stringify(body) : undefined });
      if (ok) { showToast(`${action} succeeded.`); fetchIncentives(incentiveFilter || undefined); }
      else showToast(data.error || `${action} failed.`, false);
    } finally {
      setActionLoading(null);
    }
  };

  const promptAndDo = (action: string, cb: (reason: string) => void) => {
    const reason = window.prompt(`Reason for "${action}" (required):`);
    if (reason?.trim()) cb(reason.trim());
  };

  return (
    <>
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif', padding: 24, minHeight: '100vh', background: '#f8fafc' }}>
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '12px 20px', borderRadius: 10,
          background: toast.ok ? '#dcfce7' : '#fee2e2', color: toast.ok ? '#166534' : '#991b1b', fontWeight: 600, fontSize: 14,
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        }}>
          {toast.msg}
        </div>
      )}

      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', margin: 0 }}>Realtor Management</h1>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(['list', 'incentives', 'disputes'] as const).map((v) => (
              <button key={v} onClick={() => setView(v)} style={{
                padding: '8px 18px', borderRadius: 8, border: '2px solid', cursor: 'pointer',
                borderColor: view === v ? BRAND : '#e2e8f0',
                background: view === v ? BRAND : '#fff',
                color: view === v ? '#fff' : '#64748b',
                fontWeight: 600, fontSize: 14,
              }}>
                {v === 'list' ? 'Realtors' : v === 'incentives' ? 'Incentive Queue' : 'Disputes'}
              </button>
            ))}
            <button onClick={export1099} style={{ padding: '8px 18px', borderRadius: 8, border: '2px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
              Export 1099 CSV
            </button>
          </div>
        </div>

        {view === 'list' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <button onClick={fetchRealtors} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '2px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: 14 }}>
                <RefreshCw size={14} /> Refresh
              </button>
            </div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} /></div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      {['Email', 'Brokerage', 'Status', 'W-9', 'Risk', 'Abuse', 'Quotes', 'Incentives', 'Actions'].map((h) => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {realtors.map((r: RealtorSummary & { w9Status?: string }) => (
                      <tr key={r.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '12px 16px', fontSize: 14, color: '#0f172a' }}>{r.email}</td>
                        <td style={{ padding: '12px 16px', fontSize: 14, color: '#64748b' }}>{r.brokerageName || '—'}</td>
                        <td style={{ padding: '12px 16px' }}><Badge status={r.status} /></td>
                        <td style={{ padding: '12px 16px', fontSize: 13 }}>
                          {r.w9Status ? <Badge status={r.w9Status} /> : <span style={{ color: '#94a3b8' }}>—</span>}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 14 }}>{r.riskScore}</td>
                        <td style={{ padding: '12px 16px' }}>{r.abuseFlag ? <AlertTriangle size={16} color="#ef4444" /> : '—'}</td>
                        <td style={{ padding: '12px 16px', fontSize: 14 }}>{r.quoteCount}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13 }}>
                          {Object.entries(r.incentiveSummary).map(([s, c]) => (
                            <span key={s} style={{ marginRight: 6 }}>{s}: {c}</span>
                          ))}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {r.status === 'UNVERIFIED' && (
                              <button onClick={() => realtorAction(r.userId, 'verify')} disabled={!!actionLoading} style={actionBtn('#22c55e')}>
                                {actionLoading === `${r.userId}-verify` ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle size={12} />} Verify
                              </button>
                            )}
                            {r.w9Status === 'SUBMITTED_PENDING_REVIEW' && (
                              <>
                                <button onClick={() => w9Action(r.userId, 'verify')} disabled={!!actionLoading} style={actionBtn('#22c55e')}>
                                  {actionLoading === `${r.userId}-w9-verify` ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle size={12} />} Verify W-9
                                </button>
                                <button onClick={() => w9Action(r.userId, 'reject')} disabled={!!actionLoading} style={actionBtn('#ef4444')}>
                                  Reject W-9
                                </button>
                              </>
                            )}
                            {['UNVERIFIED', 'ACTIVE'].includes(r.status) && (
                              <button onClick={() => promptAndDo('suspend', (reason) => realtorAction(r.userId, 'suspend', { reason }))} disabled={!!actionLoading} style={actionBtn('#f59e0b')}>
                                Suspend
                              </button>
                            )}
                            {r.status === 'SUSPENDED' && (
                              <button onClick={() => realtorAction(r.userId, 'reinstate')} disabled={!!actionLoading} style={actionBtn('#3b82f6')}>
                                Reinstate
                              </button>
                            )}
                            {r.status !== 'TERMINATED' && (
                              <button onClick={() => promptAndDo('terminate', (reason) => realtorAction(r.userId, 'terminate', { reason }))} disabled={!!actionLoading} style={actionBtn('#ef4444')}>
                                <XCircle size={12} /> Terminate
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {realtors.length === 0 && (
                      <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No realtors found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {view === 'incentives' && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {['', 'ELIGIBLE', 'APPROVED', 'PAID', 'CLAWED_BACK'].map((s) => (
                <button key={s} onClick={() => setIncentiveFilter(s)} style={{
                  padding: '7px 14px', borderRadius: 8, border: '2px solid',
                  borderColor: incentiveFilter === s ? BRAND : '#e2e8f0',
                  background: incentiveFilter === s ? BRAND : '#fff',
                  color: incentiveFilter === s ? '#fff' : '#64748b',
                  fontWeight: 600, fontSize: 13, cursor: 'pointer',
                }}>
                  {s || 'All'}
                </button>
              ))}
              <button onClick={() => fetchIncentives(incentiveFilter || undefined)} style={{ padding: '7px 14px', borderRadius: 8, border: '2px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: 13 }}>
                <RefreshCw size={13} style={{ display: 'inline', marginRight: 4 }} />Refresh
              </button>
            </div>
            {incentivesLoading ? (
              <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} /></div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      {['Realtor', 'Project', 'Amount', 'Status', 'Hold Releases', 'Dispute', 'Actions'].map((h) => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {incentives.map((row) => {
                      const holdOk = !row.holdReleasesAt || new Date() >= new Date(row.holdReleasesAt);
                      return (
                        <tr key={row.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '12px 16px', fontSize: 14 }}>
                            {row.realtorUser?.email}<br />
                            <span style={{ fontSize: 12, color: '#94a3b8' }}>{row.realtorUser?.realtorProfile?.brokerageName}</span>
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748b' }}>{row.project?.address || row.projectId}</td>
                          <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 600 }}>${(row.amountCents / 100).toFixed(2)}</td>
                          <td style={{ padding: '12px 16px' }}><Badge status={row.status} /></td>
                          <td style={{ padding: '12px 16px', fontSize: 13 }}>
                            {row.holdReleasesAt ? (
                              holdOk
                                ? <span style={{ color: '#22c55e' }}><CheckCircle size={14} style={{ display: 'inline', marginRight: 4 }} />Elapsed</span>
                                : <span style={{ color: '#f59e0b' }}><Clock size={14} style={{ display: 'inline', marginRight: 4 }} />{new Date(row.holdReleasesAt).toLocaleDateString()}</span>
                            ) : '—'}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 13 }}>
                            {row.dispute ? <Badge status={row.dispute.status} /> : '—'}
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {row.status === 'ELIGIBLE' && holdOk && (
                                <button onClick={() => incentiveAction(row.id, 'approve')} disabled={!!actionLoading} style={actionBtn('#22c55e')}>
                                  <CheckCircle size={12} /> Approve
                                </button>
                              )}
                              {row.status === 'APPROVED' && (
                                <button onClick={() => {
                                  const method = window.prompt('Payout method (e.g. manual, stripe_connect):');
                                  if (method?.trim()) {
                                    const extId = window.prompt('External payout ID (optional):') || undefined;
                                    incentiveAction(row.id, 'record-payout', { payoutMethod: method.trim(), externalPayoutId: extId });
                                  }
                                }} disabled={!!actionLoading} style={actionBtn('#8b5cf6')}>
                                  <DollarSign size={12} /> Mark Paid
                                </button>
                              )}
                              {['ELIGIBLE', 'APPROVED'].includes(row.status) && (
                                <button onClick={() => promptAndDo('deny', (reason) => incentiveAction(row.id, 'deny', { reason }))} disabled={!!actionLoading} style={actionBtn('#94a3b8')}>
                                  Deny
                                </button>
                              )}
                              {['ELIGIBLE', 'APPROVED', 'PAID'].includes(row.status) && (
                                <button onClick={() => promptAndDo('clawback', (reason) => incentiveAction(row.id, 'clawback', { reason }))} disabled={!!actionLoading} style={actionBtn('#ef4444')}>
                                  Clawback
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {incentives.length === 0 && (
                      <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No incentive records.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

        {view === 'disputes' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <button onClick={fetchDisputes} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '2px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: 14 }}>
                <RefreshCw size={14} /> Refresh
              </button>
            </div>
            {disputesLoading ? (
              <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} /></div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      {['Realtor', 'Incentive', 'Reason', 'Status', 'Opened', 'Actions'].map((h) => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {disputes.map((d) => (
                      <tr key={d.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '12px 16px', fontSize: 14 }}>{d.realtorUser?.email || '—'}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748b' }}>
                          ${((d.ledger?.amountCents ?? 0) / 100).toFixed(2)}<br />
                          <span style={{ fontSize: 11, color: '#94a3b8' }}>{d.ledger?.projectId?.slice(0, 8)}…</span>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 13, maxWidth: 240 }}>{d.reason}</td>
                        <td style={{ padding: '12px 16px' }}><Badge status={d.status} /></td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748b' }}>{new Date(d.createdAt).toLocaleDateString()}</td>
                        <td style={{ padding: '12px 16px' }}>
                          {d.status === 'OPEN' && (
                            <button onClick={() => resolveDispute(d.id)} disabled={!!actionLoading} style={actionBtn(BRAND)}>
                              {actionLoading === `dispute-${d.id}` ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : null}
                              Resolve
                            </button>
                          )}
                          {d.status !== 'OPEN' && <span style={{ fontSize: 12, color: '#94a3b8' }}>Resolved</span>}
                        </td>
                      </tr>
                    ))}
                    {disputes.length === 0 && (
                      <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No disputes found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
};

function actionBtn(color: string): React.CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '5px 10px', borderRadius: 6, border: `1.5px solid ${color}`,
    background: '#fff', color: color, fontSize: 12, fontWeight: 600,
    cursor: 'pointer', whiteSpace: 'nowrap',
  };
}

export default AdminRealtorsPage;
// CB:END fr-034-admin-realtors-page
